import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { translate } from '@jsverse/transloco';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { RunbookService } from 'src/app/services/api/runbook.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { ComponentService } from 'src/app/services/api/component.service';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { Constants } from 'src/app/models/constants';


@Component({
  selector: 'app-update-runbook-version',
  templateUrl: './update-runbook-version.component.html',
  styleUrls: ['./update-runbook-version.component.scss'],
  standalone: false
})
export class UpdateRunbookVersionComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() runbookData: any;
  @Output() updateRunbookEmit = new EventEmitter();
  versionUpdateForm!: FormGroup;

  title!: string;
  name!: string;
  componentType!: string;
  currentVersion!: string;
  componentsLoading:boolean = true;

  versions: Array<{ version: string }> = [];

  Constants = Constants;

  constructor(
    public activatedRoute: ActivatedRoute,
    public sidenavService: SidenavService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private runbookService: RunbookService,
    private toastrService: CustomToastrService,
    private componentService: ComponentService
  ) { }

  ngOnInit(): void {
    this.title = translate('RUNBOOK.VERSION_UPDATE');
    this.initViewData();
    this.createForm();
    this.getComponents();
  }

  private initViewData(): void {
    this.name = this.runbookData?.name;
    this.componentType = "runbook";
    this.currentVersion = this.runbookData?.componentVersion;
  }

  private createForm(): void {
    this.versionUpdateForm = this.fb.group({
      name: [{ value: this.runbookData?.name, disabled: true }],
      componentType: [{ value: this.runbookData?.type, disabled: true }],
      currentVersion: [{ value: this.runbookData?.componentVersion, disabled: true }],
      version: [
        '',
        Validators.required
      ]
    });
  }

  confirmSubmit(): void {
    if (this.versionUpdateForm.invalid) {
      this.versionUpdateForm.markAllAsTouched();
      return;
    }

    this.dialog
      .open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('RUNBOOK.VERSION_UPDATE'),
          message: translate('RUNBOOK.UPDATE_CONFIRMATION', { runbookName: this.name}),
          confirmButtonText: translate('DIALOG.CONFIRM')
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (!confirm) {
          return;
        }

        this.sidenavService.closeRightSidenav({ updated: true });

        const payload = {
          id: this.runbookData.instanceId,
          runbookName: this.runbookData.name,
          componentVersion: this.versionUpdateForm.value.version
        };

        this.runbookService
          .patchRunbookVersion(payload)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: data => {
              if (data?.httpStatusCode === 202) {
                setTimeout(() => {
                  const title = translate('RUNBOOK.RUNBOOK');
                  const message = translate('RUNBOOK.UPDATE_RUNBOOK_TRIGGERED');
                  this.toastrService.successMessage(message, title);
                }, 500);
              }
            },
            error: error => this.openErrorDialog(error)
          });
      });
  }

  getComponents(): void {
    const componentType = 'runbook';
    this.componentsLoading = true;
    this.componentService
      .getComponents(componentType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: components => {
          if (!this.runbookData?.componentName) {
            return;
          }
           this.componentsLoading = false;

          const selected = components.find(
            c =>
              (c.name === this.runbookData.componentName ||
                c.displayName === this.runbookData.displayName) &&
              c.version === this.runbookData.componentVersion
          );

          if (selected) {
            this.versions = components.filter(
              c =>
                c.name === selected.name &&
                c.displayName === selected.displayName &&
                c.version !== this.runbookData.componentVersion
            );
          }
        },
        error: error => {
          this.componentsLoading = false;
          const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
          this.dialog.open(ErrorDialogComponent, {
            minHeight: '280px',
            width: '630px',
            panelClass: 'flex-dialog',
            data: {
              title: translate('CUSTOM_IMAGES.GET_COMPONENTS_FAILED'),
              description: errorMessage
            }
          });
        }
      });
  }

  openErrorDialog(error: any): void {
    this.dialog.open(ErrorDialogComponent, {
      minHeight: '342px',
      width: '630px',
      panelClass: 'flex-dialog',
      data: {
        title: translate('SCHEDULE_DETAILS.SCHEDULE_VALIDATION_ERROR'),
        descriptionError: error
      }
    });
  }
}
